import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
  MedicineBreakdownItem,
  HqPerformanceItem,
  ManagerHqPerformanceItem,
  DoctorPerformanceItem,
} from '../reports/reports-api.service';

export interface OrganizationSummary {
  total_revenue: number;
  total_patients: number;
  total_users: number;
}

export interface DashboardFilterOptions {
  zones: string[];
  headquarters: string[];
  states: string[];
  cities: string[];
  mrs: { id: string; name: string }[];
  medicines: string[];
  doctors: { id: string; name: string; doctor_id: string }[];
  manager_hqs: { id: string; name: string }[];
  periods: string[];
}

export interface DashboardOverview {
  total_patients: number;
  enrolled_patients: number;
  enrollment_rate: number;
  medicine_orders: number;
  test_bookings: number;
  active_mrs: number;
  total_mrs: number;
  total_doctors: number;
  total_hospitals: number;
  total_medications: number;
  current_month_revenue: number;
  avg_compliance_rate: number;
  medicine_wise_patients?: MedicineBreakdownItem[];
  hq_wise_patients?: HqPerformanceItem[];
  manager_hq_wise_patients?: ManagerHqPerformanceItem[];
  dr_wise_patients?: DoctorPerformanceItem[];
}

@Injectable({
  providedIn: 'root'
})
export class DashboardApiService {
  private readonly apiUrl = environment.apiUrl;

  private filterOptions$?: Observable<DashboardFilterOptions>;

  constructor(private http: HttpClient) {}

  /**
   * Get all available filter options (zones, HQs, states, cities, MRs, ...)
   * GET /api/v1/dashboard/filters
   */
  getFilterOptions(): Observable<DashboardFilterOptions> {
    if (!this.filterOptions$) {
      this.filterOptions$ = this.http
        .get<DashboardFilterOptions>(`${this.apiUrl}/dashboard/filters`)
        .pipe(shareReplay({ bufferSize: 1, refCount: false }));
    }
    return this.filterOptions$;
  }

  /**
   * Get comprehensive dashboard overview metrics
   * GET /api/v1/dashboard/overview
   */
  getDashboardOverview(filters: any = {}): Observable<DashboardOverview> {
    // Clean up empty filters
    const params: any = {};
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        params[key] = filters[key];
      }
    });

    return this.http.get<DashboardOverview>(`${this.apiUrl}/dashboard/overview`, { params });
  }

  /**
   * Get list of hospitals associated with a given HQ
   * GET /api/v1/dashboard/hospitals-by-hq
   */
  getHospitalsByHq(hq: string): Observable<{ hq_name: string; hospitals: any[] }> {
    return this.http.get<{ hq_name: string; hospitals: any[] }>(`${this.apiUrl}/dashboard/hospitals-by-hq`, {
      params: { hq }
    });
  }

  /**
   * Get list of doctors linked to a specific hospital
   * GET /api/v1/dashboard/doctors-by-hospital
   */
  getDoctorsByHospital(hospitalId: string): Observable<{ hospital_id: string; hospital_name: string; doctors: any[] }> {
    return this.http.get<{ hospital_id: string; hospital_name: string; doctors: any[] }>(`${this.apiUrl}/dashboard/doctors-by-hospital`, {
      params: { hospital_id: hospitalId }
    });
  }
}
